import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box, Typography, CircularProgress, Button, Checkbox,
  useTheme, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { GET_COMMON_EXCEPTIONS, CREATE_COMMON_EXCEPTION, DELETE_COMMON_EXCEPTION } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { CheckStatus } from "../../../../utils/enums";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import { useToday } from "../../../../utils/dateUtils";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import { DeleteExceptionDialog } from "../../../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/DeleteExceptionDialog";
import { ExceptionDetailsForm } from "../../../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/ExceptionDetailsForm";
import { buttonBaseStyles } from "../InfraResources/shared/sharedStyles";
import ResourcesTableContainer from "../common/ResourcesTableContainer";
import BulkDeleteExceptionDialog from "../common/BulkDeleteExceptionDialog";
import ExceptionActionMenu from "../common/ExceptionActionMenu";
import { Badge as BadgeIcon } from "@mui/icons-material";
import ResourceSelectCell from "../common/ResourceSelectCell";
import ResourceStatusCell from "../common/ResourceStatusCell";
import ResourceReasonCell from "../common/ResourceReasonCell";
import ResourceNameCell from "../common/ResourceNameCell";
import DefaultCell from "../common/DefaultCell";

const GenericResourcesTab = ({ checkDetails, activeCheckId, onRefetch, onStartPolling, globalException, isPollingRunStatus = false }) => {
  const theme = useTheme();
  const today = useToday();
  
  const resources = checkDetails?.resources || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResources, setSelectedResources] = useState([]);
  const [createExceptionOpen, setCreateExceptionOpen] = useState(false);
  const [exceptionFormData, setExceptionFormData] = useState({ reason: "", validUntil: "" });
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exceptionToDelete, setExceptionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [tableFilters, setTableFilters] = useState({});

  const { data: exceptionsData, refetch: refetchExceptions } = useQuery(GET_COMMON_EXCEPTIONS, {
    variables: {
      filters: { active_check_id: activeCheckId },
      page: 1,
      limit: 1000,
    },
    skip: !activeCheckId,
  });

  const [createException, { loading: creatingException }] = useMutation(CREATE_COMMON_EXCEPTION);
  const [deleteException] = useMutation(DELETE_COMMON_EXCEPTION);

  useEffect(() => {
    if (refetchExceptions) {
      refetchExceptions();
    }
  }, [globalException?.id, refetchExceptions]);

  const mapResources = useCallback((resourcesData, exceptions) => {
    if (!resourcesData || resourcesData.length === 0) return [];

    const activeExceptions = exceptions?.data?.filter(exc => 
      exc.is_active && 
      exc.scope === "resource" && 
      exc.resource_id
    ) || [];

    return resourcesData.map((res) => {
      const resourceId = String(res.resource_id || "");
      const exception = activeExceptions.find(exc => 
        String(exc.resource_id || "") === resourceId
      );

      return {
        id: res.id,
        resourceId: resourceId,
        name: res.resource_name || res.resource_id || "-",
        resourceType: res.resource_type || "-",
        status: globalException || exception ? CheckStatus.DISABLED : (res.status?.toLowerCase() || CheckStatus.PENDING),
        reason: globalException ? "-" : (exception ? (exception.reason || "-") : (res.status_reason || "-")),
        exceptionId: exception?.id || null,
        exceptionData: exception || null,
        exceptionValidUntil: exception?.exception_valid_till || null,
      };
    });
  }, [globalException]);

  const mappedResources = useMemo(() => {
    const exceptions = exceptionsData?.getCommonExceptions || { data: [] };
    return mapResources(resources, exceptions);
  }, [resources, exceptionsData, mapResources]);

  const normalizeStatus = (status = "") => {
    const normalized = status?.toLowerCase() || "";
    if (normalized.includes(CheckStatus.PASS)) return CheckStatus.PASS;
    if (normalized.includes(CheckStatus.FAIL)) return CheckStatus.FAIL;
    if (normalized.includes(CheckStatus.DISABLED)) return CheckStatus.DISABLED;
    return CheckStatus.PENDING;
  };

  const filteredResources = useMemo(() => {
    if (!mappedResources) return [];
    let filtered = mappedResources;
    
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((res) => 
        res?.name?.toLowerCase().includes(query) || 
        (res?.resourceId && res.resourceId.toString().toLowerCase().includes(query))
      );
    }
    
    const statusFilters = tableFilters?.status || [];
    if (statusFilters.length > 0) {
      filtered = filtered.filter((res) => statusFilters.includes(normalizeStatus(res?.status)));
    }
    
    return filtered;
  }, [mappedResources, searchTerm, tableFilters]);

  const handleSelectResource = (resourceId) => {
    setSelectedResources(prev => 
      prev.includes(resourceId) 
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const handleSelectAll = () => {
    setSelectedResources(
      selectedResources.length === filteredResources.length 
        ? [] 
        : filteredResources.map((res) => res.resourceId)
    );
  };

  const handleOpenCreateException = () => {
    setCreateExceptionOpen(true);
  };

  const handleCloseCreateException = () => {
    setCreateExceptionOpen(false);
    setExceptionFormData({ reason: "", validUntil: "" });
  };

  const handleCreateException = async () => {
    if (!exceptionFormData.reason.trim()) {
      setSnackbarInfo({ message: "Please provide justification", vertical: "top", horizontal: "right" });
      return;
    }
    if (!exceptionFormData.validUntil) {
      setSnackbarInfo({ message: "Please provide expiration date", vertical: "top", horizontal: "right" });
      return;
    }
    if (exceptionFormData.validUntil < today) {
      setSnackbarInfo({ message: "Expiration date cannot be in the past", vertical: "top", horizontal: "right" });
      return;
    }

    try {
      const result = await createException({
        variables: {
          active_check_id: activeCheckId,
          scope: "resource",
          reason: exceptionFormData.reason,
          exception_valid_till: exceptionFormData.validUntil,
          resources: selectedResources.map((resourceId) => ({
            resource_id: String(resourceId),
          })),
        },
      });

      const allSuccessful = !!result?.data?.createCommonException?.success;

      if (allSuccessful) {
        setSnackbarInfo({ 
          message: `Exception created for ${selectedResources.length} resource${selectedResources.length > 1 ? 's' : ''}!`, 
          vertical: "top", 
          horizontal: "right" 
        });
        setSelectedResources([]);
        handleCloseCreateException();
        await refetchExceptions();
        if (onStartPolling) onStartPolling();
        if (onRefetch) await onRefetch();
      } else {
        setSnackbarInfo({ message: "Failed to create exception", vertical: "top", horizontal: "right" });
      }
    } catch (error) {
      setSnackbarInfo({ message: error?.message || "Failed to create exception", vertical: "top", horizontal: "right" });
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteException({ variables: { ids: [Number(exceptionToDelete?.id)] } });
      setDeleteDialogOpen(false);
      setExceptionToDelete(null);
      setSnackbarInfo({
        message: "Exception deleted successfully!",
        vertical: "top",
        horizontal: "right",
      });
      await refetchExceptions();
      if (onStartPolling) onStartPolling();
      if (onRefetch) await onRefetch();
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to delete exception.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const selectedExceptionRows = useMemo(() => {
    if (!selectedResources?.length) return [];
    const byId = new Map(
      mappedResources.map((res) => [res.resourceId || res.id, res])
    );
    return selectedResources
      .map((rid) => byId.get(rid))
      .filter((res) => res && res.exceptionId != null && res.exceptionData);
  }, [selectedResources, mappedResources]);

  const handleConfirmBulkDelete = async () => {
    const ids = selectedExceptionRows
      .map((res) => Number(res.exceptionData?.id))
      .filter((id) => !Number.isNaN(id));
    if (!ids.length) return;
    setDeleteLoading(true);
    try {
      await deleteException({ variables: { ids } });
      setBulkDeleteOpen(false);
      setSelectedResources([]);
      setSnackbarInfo({
        message: `${ids.length} exception${ids.length > 1 ? "s" : ""} deleted successfully!`,
        vertical: "top",
        horizontal: "right",
      });
      await refetchExceptions();
      if (onStartPolling) onStartPolling();
      if (onRefetch) await onRefetch();
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to delete exceptions.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTableFilterChange = (newFilters) => {
    setTableFilters(newFilters);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setTableFilters({});
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const resourceColumns = [
    {
      label: (
        <Checkbox
          checked={selectedResources.length === filteredResources.length && filteredResources.length > 0}
          indeterminate={selectedResources.length > 0 && selectedResources.length < filteredResources.length}
          onChange={handleSelectAll}
          disabled={!!globalException}
          sx={{ color: "#031B89", "&.Mui-checked": { color: "#031B89" }, "&.MuiCheckbox-indeterminate": { color: "#031B89" } }}
        />
      ),
      field: "select",
      width: "7%",
      align: "center"
    },
    { label: "Resource Name", field: "name", align: "left", width: "30%" },
    { label: "Type", field: "resourceType", align: "center", width: "15%" },
    { 
      label: "Status", 
      field: "status", 
      align: "center", 
      width: "10%",
      filterConfig: {
        filterOptions: [
          { id: CheckStatus.PASS, label: "Passing" },
          { id: CheckStatus.FAIL, label: "Failing" },
          { id: CheckStatus.PENDING, label: "Pending" },
          { id: CheckStatus.DISABLED, label: "Disabled" },
        ],
      },
    },
    { label: "Reason", field: "reason", align: "left", width: "35%" },
  ];

  const renderResourceCell = (row, column) => {
    if (column?.field === "select") {
      return (
        <ResourceSelectCell
          resourceId={row.resourceId || row.id}
          selectedResources={selectedResources}
          onSelect={handleSelectResource}
          disabled={!!globalException}
        />
      );
    }
    if (column?.field === "status") {
      return (
        <ResourceStatusCell 
          status={row.status} 
          exceptionValidUntil={row.exceptionValidUntil}
          isRefreshing={isPollingRunStatus}
        />
      );
    }
    if (column?.field === "reason") {
      const hasException = row?.exceptionId && row?.exceptionData;
      const isResourceSelected = selectedResources.includes(row.resourceId || row.id);
      const isBulkMode = selectedResources.length >= 1;
      const isDeleteDisabled =
        !hasException ||
        (isBulkMode && isResourceSelected) ||
        !!globalException ||
        isPollingRunStatus;

      return (
        <ResourceReasonCell
          reason={row.reason}
          hasException={hasException}
          onDelete={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isDeleteDisabled) {
              setExceptionToDelete(row.exceptionData);
              setDeleteDialogOpen(true);
            }
          }}
          disabled={isDeleteDisabled}
          hasGlobalException={!!globalException}
          isBulkMode={isBulkMode}
          isResourceSelected={isResourceSelected}
        />
      );
    }
    if (column?.field === "name") {
      return <ResourceNameCell value={row.name} />;
    }
    return <DefaultCell value={row[column?.field]} />;
  };

  return (
    <>
      <ResourcesTableContainer
        title="Resources"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={clearFilters}
        filteredCount={filteredResources.length}
        tableExpanded={tableExpanded}
        onToggleExpand={() => setTableExpanded(!tableExpanded)}
        selectedCount={selectedResources.length}
        selectedLabel="Selected"
        markExceptionButton={
          !globalException && (
            <ExceptionActionMenu
              disabled={
                selectedResources.length === 0 ||
                creatingException ||
                isPollingRunStatus
              }
              disabledTooltip={
                isPollingRunStatus
                  ? "Please wait for the check to finish updating"
                  : selectedResources.length === 0
                  ? "Select at least one resource to manage exceptions"
                  : ""
              }
              onMark={() => handleOpenCreateException()}
              markDisabled={creatingException}
              markDescription="Create exception for selected resources"
              onDelete={() => setBulkDeleteOpen(true)}
              deleteDisabled={
                selectedExceptionRows.length === 0 ||
                deleteLoading ||
                isPollingRunStatus
              }
              deleteCount={selectedExceptionRows.length}
              deleteDescription={
                selectedExceptionRows.length === 0
                  ? "No selected resources have an existing exception"
                  : "Remove existing exception(s)"
              }
            />
          )
        }
      >
        <ReusableTable 
          columns={resourceColumns} 
          data={filteredResources} 
          renderCell={renderResourceCell} 
          showPagination={true}
          height="auto"
          rowsPerPageOptions={[50, 100]}
          filters={tableFilters}
          onFilterChange={handleTableFilterChange}
        />
      </ResourcesTableContainer>

      {deleteDialogOpen && exceptionToDelete && (
        <DeleteExceptionDialog
          deleteDialogOpen={deleteDialogOpen}
          setDeleteDialogOpen={setDeleteDialogOpen}
          exceptionToDelete={exceptionToDelete}
          submitLoading={deleteLoading}
          handleConfirmDelete={handleConfirmDelete}
        />
      )}

      <BulkDeleteExceptionDialog
        open={bulkDeleteOpen}
        onCancel={() => setBulkDeleteOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        loading={deleteLoading}
        count={selectedExceptionRows.length}
        previewItems={selectedExceptionRows.map((res) => ({
          label:
            res.name || res.resourceId || res.exceptionData?.resource_id,
          reason: res.exceptionData?.reason,
          validUntil: res.exceptionData?.exception_valid_till,
        }))}
        entityLabel="exception"
        entityIcon={BadgeIcon}
      />

      <Dialog
        open={createExceptionOpen} 
        onClose={handleCloseCreateException} 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: 600,
            maxWidth: 700,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2,
            px: 3,
          }}
        >
          Create Exception for {selectedResources.length} Resource{selectedResources.length !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 0,
            "&.MuiDialogContent-root": {
              paddingTop: 3,
            },
          }}
        >
          <ExceptionDetailsForm
            reason={exceptionFormData.reason}
            setReason={(val) => setExceptionFormData({ ...exceptionFormData, reason: val })}
            validUntil={exceptionFormData.validUntil}
            setValidUntil={(val) => setExceptionFormData({ ...exceptionFormData, validUntil: val })}
            isValidUntilPast={exceptionFormData.validUntil && exceptionFormData.validUntil < today}
          />
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
            pt: 2,
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            mt: 1,
          }}
        >
          <Button
            onClick={handleCloseCreateException}
            disabled={creatingException}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: theme.customColors.border.strong,
                backgroundColor: theme.customColors.background.hover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateException}
            variant="contained"
            disabled={!exceptionFormData.reason?.trim() || !exceptionFormData.validUntil || (exceptionFormData.validUntil && exceptionFormData.validUntil < today) || creatingException}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              bgcolor: theme.customColors.button.primary,
              color: theme.customColors.button.text,
              "&:hover": {
                bgcolor: theme.palette._blue.dark,
              },
              "&:disabled": {
                bgcolor: theme.customColors.background.light,
                color: theme.customColors.text.disabled,
              },
            }}
          >
            {creatingException ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbarInfo && (
        <ConfirmSnackbar
          open={!!snackbarInfo}
          message={snackbarInfo.message}
          vertical={snackbarInfo.vertical || "top"}
          horizontal={snackbarInfo.horizontal || "right"}
          onClose={() => setSnackbarInfo(null)}
        />
      )}
    </>
  );
};

export default GenericResourcesTab;
